<script setup>
import { ref, onMounted, onBeforeUnmount, defineProps, defineEmits, defineExpose } from 'vue'
import EditorJsEditor from '@/components/EditorJs/EditorJsEditor.vue'

const props = defineProps({
  initialData: {
    type: Object,
    default: null
  },
  placeholder: {
    type: String,
    default: 'Start writing...'
  },
  headerPlaceholder: {
    type: String,
    default: 'Title...'
  },
  loading: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['change', 'save'])

const editorRef = ref(null)

// Keyboard shortcut handler (Ctrl+S / Cmd+S)
function handleKeydown(event) {
  if ((event.ctrlKey || event.metaKey) && event.key === 's') {
    event.preventDefault()
    emit('save')
  }
}

// Save method - returns editor content
async function save() {
  if (!editorRef.value) {
    return null
  }
  try {
    const content = await editorRef.value.save()
    return content
  } catch (error) {
    console.error('DocumentEditor save error:', error)
    return null
  }
}

// Alias for save
async function getContent() {
  return save()
}

// Focus the editor at the end
async function focus() {
  if (editorRef.value) {
    await editorRef.value.focus()
  }
}

onMounted(() => {
  document.addEventListener('keydown', handleKeydown)
})

onBeforeUnmount(() => {
  document.removeEventListener('keydown', handleKeydown)
})

defineExpose({
  save,
  getContent,
  focus
})
</script>

<template>
  <div class="document-editor">
    <!-- Loading Overlay -->
    <div v-if="loading" class="loading-overlay">
      <font-awesome-icon :icon="['fas', 'spinner']" spin class="loading-icon" />
    </div>

    <!-- Editor Content -->
    <div class="editor-content">
      <EditorJsEditor
        ref="editorRef"
        :initialData="initialData"
        :placeholder="placeholder"
        :headerPlaceholder="headerPlaceholder"
        @change="emit('change')"
      />
    </div>
  </div>
</template>

<style scoped>
.document-editor {
  width: 100%;
  position: relative;
  background: transparent;
}

/* Loading Overlay */
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(23, 28, 36, 0.8);
  z-index: 100;
}

.loading-icon {
  font-size: 2rem;
  color: #7357D2;
}

</style>
